// Onboarding state — the resumable record of how far a creator got
// through the wizard. Persisted to OPFS-SQLite so a reload (or a
// crash mid-wizard) resumes at the right step rather than starting
// over (04_CREATOR_ONBOARDING.md → Failure handling).
//
// Keyed by the Google subject id, so each signed-in identity has its
// own onboarding row. NOTE: secrets (GitHub / Cloudflare tokens) are
// deliberately NOT stored here — they live in the localStorage vault
// (lib/secrets.ts), matching 08_AUTHORING_APP.md ("CF + GitHub tokens
// (localStorage, treated as secrets)"). This table holds only the
// non-sensitive structural state needed to resume.

import { exec, query } from "./sqlite";

/** Highest wizard step the creator has completed. Steps mirror
 *  04_CREATOR_ONBOARDING.md. Sprint 1 reaches step 4 (repos
 *  provisioned); Sprint 2 continues 5-9. */
export const STEP = {
  NONE: 0,
  GOOGLE: 1,
  GITHUB: 2,
  HANDLE: 3,
  REPOS: 4,
  CF_CONNECTED: 5,
  CF_PAGES: 6,
  DOMAIN: 7,
  INDEXER: 8,
  PUBLISHED: 9,
} as const;

/** Sprint 1's finish line: GitHub connected, handle chosen, both
 *  repos provisioned. Everything past this is Sprint 2+. */
export const SPRINT1_DONE_STEP = STEP.REPOS;

export interface Onboarding {
  google_sub: string;
  step: number;
  handle: string | null;
  github_login: string | null;
  private_repo: string | null;
  public_repo: string | null;
  cf_account_id: string | null;
  cf_pages_project: string | null;
  custom_domain: string | null;
  indexer_registered: number;
  created_at: number;
  updated_at: number;
}

let initialized = false;

async function ensureTable(): Promise<void> {
  if (initialized) return;
  await exec(`
    CREATE TABLE IF NOT EXISTS onboarding (
      google_sub        TEXT PRIMARY KEY,
      step              INTEGER NOT NULL DEFAULT 0,
      handle            TEXT,
      github_login      TEXT,
      private_repo      TEXT,
      public_repo       TEXT,
      cf_account_id     TEXT,
      cf_pages_project  TEXT,
      custom_domain     TEXT,
      indexer_registered INTEGER NOT NULL DEFAULT 0,
      created_at        INTEGER NOT NULL,
      updated_at        INTEGER NOT NULL
    )
  `);
  initialized = true;
}

export async function getOnboarding(googleSub: string): Promise<Onboarding | null> {
  await ensureTable();
  const rows = await query<Onboarding>(
    `SELECT * FROM onboarding WHERE google_sub = ?`,
    [googleSub],
  );
  return rows[0] ?? null;
}

/** Columns a caller is allowed to patch. `step` is monotonic — we only
 *  ever raise it (a resume should never regress progress). */
type Patchable = Partial<Omit<Onboarding, "google_sub" | "created_at" | "updated_at">>;

const PATCH_COLUMNS: (keyof Patchable)[] = [
  "step", "handle", "github_login", "private_repo", "public_repo",
  "cf_account_id", "cf_pages_project", "custom_domain", "indexer_registered",
];

/** Create-or-update the onboarding row for this identity. `step` is
 *  raised to the max of the existing and incoming value so resumes
 *  can't move the creator backwards. */
export async function saveOnboarding(googleSub: string, patch: Patchable): Promise<Onboarding> {
  await ensureTable();
  const now = Date.now();
  const existing = await getOnboarding(googleSub);

  if (!existing) {
    await exec(
      `INSERT INTO onboarding
        (google_sub, step, handle, github_login, private_repo, public_repo,
         cf_account_id, cf_pages_project, custom_domain, indexer_registered,
         created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        googleSub,
        patch.step ?? STEP.NONE,
        patch.handle ?? null,
        patch.github_login ?? null,
        patch.private_repo ?? null,
        patch.public_repo ?? null,
        patch.cf_account_id ?? null,
        patch.cf_pages_project ?? null,
        patch.custom_domain ?? null,
        patch.indexer_registered ?? 0,
        now,
        now,
      ],
    );
    return (await getOnboarding(googleSub))!;
  }

  const sets: string[] = [];
  const binds: unknown[] = [];
  for (const col of PATCH_COLUMNS) {
    if (!(col in patch)) continue;
    if (col === "step") {
      // monotonic: never regress
      sets.push(`step = MAX(step, ?)`);
      binds.push(patch.step);
    } else {
      sets.push(`${col} = ?`);
      binds.push((patch as Record<string, unknown>)[col] ?? null);
    }
  }
  sets.push(`updated_at = ?`);
  binds.push(now);
  binds.push(googleSub);

  await exec(`UPDATE onboarding SET ${sets.join(", ")} WHERE google_sub = ?`, binds);
  return (await getOnboarding(googleSub))!;
}

/** Wipe a creator's onboarding row — used by "re-run the wizard". */
export async function resetOnboarding(googleSub: string): Promise<void> {
  await ensureTable();
  await exec(`DELETE FROM onboarding WHERE google_sub = ?`, [googleSub]);
}
